/* Version 2026-10-20 update-10 */
/* Rewriting a typed search term into one this shop can answer (1-9).

   The catalogue is English, the customer often types something else, and
   Shopify will not close that gap: a word that matches nothing does not empty
   the result, it is quietly outranked (measured: `zzzznotaword` alone gives 0,
   but `battery zzzznotaword` still gives 670). So the rewriting happens before
   the term leaves the browser.

   This file is the machinery. The content sits beside it, per language, so a
   visitor downloads only what applies to them:

     phones-search-lang-<code>.js    plural to singular, from the live site
     phones-search-rules.js          the synonyms, one list for every language,
                                  written by the app
     phones-search-words.js          every word this shop itself uses

   English always rides along next to the visitor's own language: the catalogue
   is English, and people type English words in every language.

   One rule holds the whole thing together:

     A word the shop knows is never touched. A word it does not know may
     become one it does. Nothing lands, nothing changes.

   That matters because the singular rules were written for a LIKE '%word%'
   search, where a stem that falls a letter short still matches — glasplat
   finds glasplaat. Shopify matches whole words. Over the 13,787 active
   products the rules on their own rewrite 189 words and 142 of those land on a
   word that occurs nowhere: camera becomes camerum, plus becomes plu. The live
   site keeps a hand-written ignore list against exactly that; the word list
   does the same work without maintenance.

   Nothing loaded at all? Then the term goes out as it was typed. */

(function () {
  /* Which language the visitor is browsing in. The header sets it; the html
     tag is the fallback for a page that forgot to. */
  const lang = String(
    window.phonesSearchLang || document.documentElement.getAttribute('lang') || 'en',
  )
    .toLowerCase()
    .slice(0, 2);

  /* The singular pack for the language, and English behind it. Read at every
     call, not once: a shop without locale prefixes (4Phones, asked 4-9) has
     no /nl in its URL and its header may not have loaded the pack, so it is
     fetched below and lands after this script ran. The same language twice
     is the normal case for an English shopper, and the filter takes care of
     that. */
  const packsNow = () => {
    const packs = window.phonesSearch || {};
    const active = [packs[lang], lang === 'en' ? null : packs.en].filter(Boolean);
    const keep = new Set();
    for (const pack of active) for (const w of pack.keep || []) keep.add(String(w).toLowerCase());
    return { tables: active.map((p) => p.singular || []).filter((t) => t.length), keep };
  };
  /* No pack for the language on the page? Then fetch it from the same place
     this script came from — the theme names the packs phones-search-lang-<xx>.js.
     A language without a pack (de, sv) gets a 404 and nothing more; English
     alone is then what the visitor gets. */
  if (lang !== 'en' && !(window.phonesSearch || {})[lang] && document.currentScript && document.currentScript.src) {
    const el = document.createElement('script');
    el.src = document.currentScript.src.replace(/phones-search-terms\.js/, 'phones-search-lang-' + lang + '.js');
    if (el.src !== document.currentScript.src) document.head.appendChild(el);
  }

  /* One list for every language. The product names are English in every
     language version of the shop, so a rule is always a mapping from what
     somebody types — in whatever language — to a word that stands in the
     product data. Splitting it per language only meant that a Dutch visitor
     typing a German word found nothing. */
  /* Lower case without accents: hüllen and hullen, écouteur and ecouteur are
     the same typed word, and the list may hold either spelling (asked 4-9:
     "Hüllen" found no rule while "hullen" stood in the list). ß becomes ss. */
  function plain(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
  }

  const SYNONYMS = (function () {
    const out = {};
    const rules = (window.phonesSearchRules && window.phonesSearchRules.synonyms) || [];
    for (let i = 0; i < rules.length; i += 1) {
      const r = rules[i];
      if (!r || r.off || !r.from || !r.to) continue;
      const from = plain(r.from);
      /* First rule wins, as on the live site: the list has "hoesje" twice
         there and only the first one ever fired. */
      if (from && !out[from]) out[from] = String(r.to).toLowerCase().trim();
    }
    return out;
  })();

  /* What a rule points at, read as words with alternatives.

     A space means another word the product has to have as well; a bar means
     either of these will do. So the target is a small grid:

       loudspeaker|earspeaker|speaker   one word, three ways to say it —
                                        Shopify matches whole words and our
                                        star is a prefix, so nothing else finds
                                        Loudspeaker (251) from "speaker"
       charging board|flex|port|connector
                                        two words: charging, and then one of
                                        four. Just "charging" would be every
                                        wireless charger in the shop as well
                                        (1000+ against 913)
       back cover                       two words, one way each */
  const phrase = (to) =>
    String(to)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(function (part) {
        return part.split('|').map(function (s) { return s.trim(); }).filter(Boolean);
      })
      .filter(function (forms) { return forms.length; });
  /* The plainest reading of a target: the first way of saying each word. */
  const primary = (to) =>
    phrase(to)
      .map(function (forms) { return forms[0]; })
      .join(' ');

  /* Phrases before single words, and the longest phrase first — otherwise
     "oplader" eats "thuis oplader" and "lens" fires inside "camera lens". */
  const PHRASES = Object.keys(SYNONYMS)
    .filter((k) => k.indexOf(' ') >= 0)
    .sort((a, b) => b.length - a.length)
    .map((k) => [
      k,
      SYNONYMS[k],
      new RegExp('(^|\\s)' + k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?=\\s|$)', 'g'),
    ]);

  const apply = (word, table) => {
    for (let i = 0; i < table.length; i += 1)
      if (table[i][0].test(word)) return word.replace(table[i][0], table[i][1]);
    return word;
  };

  /* Every stem worth trying, best first. The live site runs one language and
     then English and stops there; that order costs it allergies → allergie, a
     word no shop sells. Offering all of them and letting the catalogue pick
     lifts the share of plurals that come back to a real word from 92% to 99%. */
  /* Never a stem under three letters (30-9). The word list holds no words
     that short by construction, so known() cannot judge one and lets it
     through — right for "z flip", where the z was typed, and wrong here,
     where the stem is what the rules made of a word. Applied twice, the
     tables wear a word down to nothing: pennen became "p", tassen "ta",
     bussen "bu", and each of those was then searched for as if the shop knew
     it. Found by the first test that ran this file against the real word
     list. */
  const MIN_STEM = 3;
  function stems(word) {
    const { tables, keep } = packsNow();
    if (keep.has(word)) return [];
    const out = [];
    const worth = (stem) => stem && stem !== word && stem.length >= MIN_STEM && out.indexOf(stem) < 0;
    const first = tables.map((t) => apply(word, t));
    for (let i = 0; i < first.length; i += 1) {
      for (let j = 0; j < tables.length; j += 1) {
        const both = apply(first[i], tables[j]);
        if (worth(both)) out.push(both);
      }
      if (worth(first[i])) out.push(first[i]);
    }
    return out;
  }

  /* The plurals of a singular: cover → covers, battery → batteries. Only a
     form the language's own rules bring back to this very word counts, so a
     guess that is a word of its own is never offered as its plural. */
  function pluralsOf(word) {
    if (!/^[a-z]{3,}$/.test(word)) return [];
    const guesses = [word + 's', word + 'es'];
    if (/[^aeiou]y$/.test(word)) guesses.push(word.slice(0, -1) + 'ies');
    return guesses.filter((form) => stems(form).indexOf(word) >= 0);
  }

  const words = () => (window.phonesSearchWords instanceof Set ? window.phonesSearchWords : null);
  /* The letters of the codes in the tags (vog of VOG-AL00), written by the
     app beside the words since 9-10. Only kept from typo correction. Not
     words: in the word list "ref" of REF-GOP6PRO01BF made the popup treat
     "ref" as finished, and refurbished was never offered. And not "known"
     either: a known word is never rewritten, which stopped the rule
     ref → refurbished (iphone 11 ref: 4 results became 292, measured 9-10). */
  const codeParts = () => (window.phonesSearchCodeParts instanceof Set ? window.phonesSearchCodeParts : null);
  const isCodePart = (w) => { const set = codeParts(); return !!set && set.has(w); };
  /* A phrase is known when every word in it is. Anything with a digit is a
     model code and is left alone — the live site skips those too. */
  function known(text) {
    const set = words();
    if (!set) return false;
    /* The word list is built from the catalogue split on anything that is
       not a letter or digit, so usb-c sits in it as usb and c. Judged whole,
       usb-c was never known and every rule pointing at it — usbc, type-c —
       was thrown away. A hyphen splits for the check, as it did for the list. */
    const parts = String(text).split(/[ -]/);
    for (let i = 0; i < parts.length; i += 1) {
      /* A position may offer several words; one of them being real is enough,
         because that is exactly what the query asks for. */
      if (parts[i] && parts[i].indexOf('|') >= 0) {
        const ways = parts[i].split('|');
        let any = false;
        for (let k = 0; k < ways.length; k += 1) if (known(ways[k])) any = true;
        if (!any) return false;
        continue;
      }
      const part = parts[i];
      if (!part || /\d/.test(part)) continue;
      /* Anything under three letters is not in the list by construction, so
         judging it would fail every phrase that holds one: "z flip" was
         rejected on the z and the rule never fired, which cost 276 products
         (measured 2-9). */
      if (part.length < 3) continue;
      if (!set.has(part)) return false;
    }
    return true;
  }

  /* Half a word finished off. The strict search matches whole words, so
     "compatibl" finds nothing where "compatible" finds the one product asked
     for — and the customer then falls back to the loose search and gets 227
     things they did not ask for (measured 1-9).

     Only when the shop has exactly one word beginning with what was typed. Two
     candidates and we would be guessing, and a wrong guess is worse here than
     no guess: with the fuzziness off it costs the whole result. */
  function complete(word) {
    if (word.length < 5 || /\d/.test(word)) return null;
    /* The words the customer types are not the words the shop uses, so both
       lists are worth finishing off: "compatibl" against the shop, "trilmot"
       against the rules, where it becomes trilmotor and then vibrator. The
       rules go first — they are the language being typed. */
    let found = null;
    for (const key in SYNONYMS) {
      if (key.length <= word.length || key.indexOf(word) !== 0) continue;
      if (found) { found = null; break; }
      found = key;
    }
    if (found) return found;
    const set = words();
    if (!set) return null;
    for (const w of set) {
      if (w.length <= word.length || w.indexOf(word) !== 0) continue;
      if (found) return null;
      found = w;
    }
    return found;
  }

  /* A typo: a word the shop does not know, one or two letters away from one
     it does. Measured 16-9: "screenprotecter iphone 16" found nothing strict
     and then 925 things loose, because the loose search simply drops the
     word it does not know; "powerbnk" gave a thousand Rixus products. So an
     unknown word is compared with every rule word (the language being
     typed) and every catalogue word, and replaced when exactly one of them
     is nearest. Guarded: four letters or more, no digits (a code is never
     corrected), not a model word, the first letter must match (a typo is
     rarely there, and it halves the candidates and the false hits), one
     letter for short words and two from seven letters. A tie means we do
     not guess — with the fuzziness off a wrong guess costs the whole
     result. A rule word wins over a catalogue word at the same distance.
     Damerau distance, so a swapped pair (dispaly) counts as one. */
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev2 = null;
    let prev = [];
    for (let j = 0; j <= b.length; j += 1) prev[j] = j;
    for (let i = 1; i <= a.length; i += 1) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[b.length];
  }
  function nearest(word) {
    if (word.length < 4 || /\d/.test(word) || MODEL_WORDS[word] || isCodePart(word)) return null;
    const max = word.length >= 7 ? 2 : 1;
    const set = words();
    const lists = [Object.keys(SYNONYMS), set ? Array.from(set) : []];
    const found = [];
    for (let l = 0; l < lists.length; l += 1) {
      const list = lists[l];
      let best = null;
      let bestD = max + 1;
      let tie = false;
      for (let i = 0; i < list.length; i += 1) {
        const w = list[i];
        if (w.length < 3 || w[0] !== word[0] || w === word) continue;
        const d = distance(word, w, max);
        if (d > max) continue;
        if (d < bestD) { bestD = d; best = w; tie = false; } else if (d === bestD) tie = true;
      }
      found.push({ best: best, d: bestD, tie: tie });
    }
    const win = found[0].d <= found[1].d ? found[0] : found[1];
    if (win.best && !win.tie) return win.best;
    /* A tie between one word in singular and plural is no tie (6-10, from
       the 4Phones session): "displayy" lies one letter from both display and
       displays, so it was never corrected and "iphone 17 pro max displayy"
       found nothing where the term without the typo gives 135. The words at
       the winning distance go through the stem rules; when they all come
       down to one word, that is the correction. Two different words still
       mean we do not guess. */
    if (win.best && win.tie) {
      const same = [];
      for (let l = 0; l < lists.length; l += 1) {
        const list = lists[l];
        for (let i = 0; i < list.length; i += 1) {
          const w = list[i];
          if (w.length < 3 || w[0] !== word[0] || w === word) continue;
          if (distance(word, w, win.d) !== win.d) continue;
          const root = stems(w).filter(known)[0] || w;
          if (same.indexOf(root) < 0) same.push(root);
        }
      }
      if (same.length === 1) return same[0];
    }
    return null;
  }

  /* The words a glued model number may be split on — brands, series and the
     little words that go with them: iphone, galaxy, pro, ultra, fe, se. EMC
     keeps the list; it rides along in the rules file. Without it we would have
     to guess, and guessing takes "rxwc13" apart into "rxwc 13". */
  const MODEL_WORDS = (function () {
    const out = {};
    const list = (window.phonesSearchRules && window.phonesSearchRules.modelWords) || [];
    for (let i = 0; i < list.length; i += 1) out[String(list[i]).toLowerCase()] = true;
    return out;
  })();

  /* Model numbers typed as one word: iphone17pro, s24fe, xiaomi15ultra.

     The rule is a letter↔digit boundary, but only where the letters are a word
     this shop actually uses. Splitting on the boundary alone would take
     "rxwc13" apart into "rxwc 13" — and rxwc13 is exactly the article number a
     customer types when they know what they want. iphone is a word here, rxwc
     is not, and that is the whole difference. */
  function splitModel(word) {
    if (!/\d/.test(word) || !/[a-z]/.test(word) || word.indexOf('-') >= 0) return null;
    /* Only EMC's model words, plus a rule that turns a glued pair into two
       words (promax → pro max). A rule that puts a space in front of a number
       is no good here: "zflip7" would come out as "z flip 7" while the shop
       writes Flip7 glued — that is what the zflip7 rules are for. */
    const useful = (part) =>
      part.length > 1 &&
      (MODEL_WORDS[part] ||
        (!!SYNONYMS[part] && !/[0-9]/.test(word.slice(part.length, part.length + 1))));

    /* In front: the longest opening that is a model word. iphone17pro splits
       after iphone, galaxya54 after galaxy — and rxwc13 nowhere, because rxwc
       is not one. */
    for (let i = word.length - 1; i >= 2; i -= 1) {
      const head = word.slice(0, i);
      if (!/^[a-z]+$/.test(head) || !useful(head)) continue;
      const rest = word.slice(i);
      return head + ' ' + (splitModel(rest) || rest);
    }
    /* Behind: a closing run of letters after the digits. 17pro, 24fe, 15promax. */
    const m = word.match(/^(.*\d)([a-z]{2,})$/);
    if (m && useful(m[2])) return m[1] + ' ' + m[2];
    return null;
  }

  function rewrite(term) {
    let text = plain(term).replace(/\s+/g, ' ').trim();
    /* A word without a letter or digit (the slash in "mi 10t / mi 10t pro",
       as model names are written) is no word: required, it found nothing
       (7-10). The own index skips it as well. */
    text = text.split(' ').filter(function (w) { return /[a-z0-9]/.test(w); }).join(' ');
    if (!text) return { words: [], changed: [] };
    const changed = [];

    /* A code of the shop (see codesFor) stays whole: splitting it as a model
       name would scatter GH82-39192C-PA into words no tag holds. */
    const codeHits = Object.create(null);
    /* Samsung prints its model codes as SM-S942B on the device; the shop
       tags them without the SM- (S942B on 33 products, SM-S942B on none,
       measured 8-10). A typed sm- code also asks the code without it, and a
       loose "sm" in front of such a code is left out. */
    const SAMSUNG = /^[a-z][0-9]{3,4}[a-z0-9]{0,2}$/;
    const raw = text.split(' ').filter(function (w, j, all) {
      return !(w === 'sm' && j + 1 < all.length && SAMSUNG.test(all[j + 1]));
    });
    text = raw.join(' ');
    for (let j = 0; j < raw.length; j += 1) {
      const m = /^sm-?([a-z][0-9]{3,4}[a-z0-9]{0,2})$/.exec(raw[j]);
      const bare = m ? m[1] : null;
      let hits = codesFor(bare || raw[j], j === raw.length - 1);
      if (bare) hits = [bare].concat(hits.filter(function (h) { return h !== bare; }));
      if (hits.length) codeHits[raw[j]] = hits;
      /* A code typed exactly as the shop spells it has no other spelling to
         add, but must stay whole all the same: without this, M2002F4LG,
         P30PLAY and ZA940124SE were split as model names on the model word
         at their end ("m2002f4 lg", 19-10), and found nothing. */
      else if (isShopCode(raw[j])) codeHits[raw[j]] = [];
    }

    /* Before anything else: a model number typed as one word is several words,
       and the rules below only ever look at whole words. */
    text = text
      .split(' ')
      .map(function (w) {
        if (codeHits[w]) return w;
        const split = splitModel(w);
        /* Every change carries what did it — model, phrase, synonym,
           singular, complete — so the search log can say whether a synonym
           rule actually did work (asked 4-9). */
        if (split) changed.push([w, split, 'model']);
        return split || w;
      })
      .join(' ');

    for (let i = 0; i < PHRASES.length; i += 1) {
      const from = PHRASES[i][0];
      const to = PHRASES[i][1];
      const re = PHRASES[i][2];
      re.lastIndex = 0;
      if (re.test(text) && text.indexOf(to) < 0) {
        re.lastIndex = 0;
        text = text.replace(re, '$1' + to);
        changed.push([from, to, 'phrase']);
      }
    }

    const out = [];
    /* Per word what the search may look for. Usually one thing; two when the
       shop uses the typed word itself AND a rule points somewhere else. */
    const alternatives = [];
    const take = (word, extra) => {
      out.push(word);
      alternatives.push(extra && extra !== word ? [word, extra] : [word]);
    };
    const parts = text.split(' ');
    for (let i = 0; i < parts.length; i += 1) {
      const word = parts[i];
      if (!word) continue;
      /* A phrase rule leaves its alternatives standing in the text, bars and
         all: "oplaad connector" becomes "charging board|flex|port|connector".
         Here that turns back into one position with four ways of saying it —
         asking for the bars as if they were a word gives nothing. */
      if (word.indexOf('|') >= 0) {
        const ways = word.split('|').filter(Boolean);
        const real = ways.filter(known);
        out.push(ways[0]);
        alternatives.push(real.length ? real : ways);
        continue;
      }
      /* A model code: hands off — unless a rule names this exact word. The
         Samsung Z series lives there: zflip7 has to become flip7, because the
         catalogue writes the number glued and "flip 7" finds one product
         against fifty. */
      if (/\d/.test(word)) {
        const named = SYNONYMS[word];
        if (named) {
          const grid = phrase(named);
          changed.push([word, primary(named), 'synonym']);
          for (let j = 0; j < grid.length; j += 1) {
            const ways = grid[j].filter(known);
            out.push(grid[j][0]);
            alternatives.push(ways.length ? ways : [grid[j][0]]);
          }
        } else {
          take(word);
        }
        continue;
      }
      /* A word the shop uses itself is never replaced — but if a rule points
         it somewhere else, both are worth looking for. "lcd" is in eleven
         product names and means display in two and a half thousand; "cover" is
         in fifteen hundred and means case in three thousand more. Replacing
         loses the first set, keeping loses the second, so we ask for both. */
      if (known(word)) {
        const other = SYNONYMS[word];
        const grid = other ? phrase(other) : [];
        /* A one-word target rides along word by word. A longer one rides
           along whole, in quotes: screenprotector → tempered glass gave 579
           against 458 without it (measured 2-9), and the acceptance list
           asks for at least 519. */
        const extra =
          grid.length === 1
            ? grid[0].filter(known)
            : grid.length > 1 && known(primary(other))
              ? [primary(other)]
              : [];
        /* A known plural rides with its known singular: "chargers" is one
           product and "charger" 181, and a shopper typing chargers means both
           (asked 4-9). The typed word stays, so nothing is lost. */
        const singular = stems(word).filter(known);
        for (let k = 0; k < singular.length; k += 1) if (extra.indexOf(singular[k]) < 0) extra.push(singular[k]);
        out.push(word);
        alternatives.push([word].concat(extra.filter(function (t) { return t !== word; })));
        continue;
      }
      /* synonym → singular → synonym, and the first form the shop knows wins */
      const candidates = [];
      /* What a candidate looked like as a target, so the winner can be sent
         out with all its alternatives. */
      const grids = {};
      /* Which step produced a candidate, for the log */
      const kinds = {};
      const offer = (to, kind) => {
        const key = primary(to);
        if (!key) return;
        candidates.push(key);
        grids[key] = phrase(to);
        if (!kinds[key]) kinds[key] = kind;
      };
      if (SYNONYMS[word]) offer(SYNONYMS[word], 'synonym');
      const forms = stems(word);
      for (let j = 0; j < forms.length; j += 1) {
        if (SYNONYMS[forms[j]]) offer(SYNONYMS[forms[j]], 'synonym');
        candidates.push(forms[j]);
        if (!kinds[forms[j]]) kinds[forms[j]] = 'singular';
      }
      let picked = null;
      for (let j = 0; j < candidates.length; j += 1)
        if (known(candidates[j])) { picked = candidates[j]; break; }
      /* Still nothing, and the shop has exactly one word that starts with it?
         Then it was half typed, and finishing it is the difference between one
         right answer and a page of wrong ones. */
      if (!picked) {
        const whole = complete(word);
        if (whole) {
          picked = whole;
          kinds[whole] = kinds[whole] || 'complete';
          if (SYNONYMS[whole] && known(SYNONYMS[whole])) {
            picked = SYNONYMS[whole];
            kinds[picked] = kinds[picked] || 'complete';
          }
        }
      }
      /* Still nothing: a typo, when one known word is clearly nearest (see
         nearest). A rule word found this way goes on through its rule, so
         "schermbeschermr" becomes screenprotector, not schermbeschermer. */
      /* Not for a last word that begins words of the shop: that is somebody
         still typing, and the query builders finish it (startsOf). When the
         word list came to hold "ref", "iphone 11 refu" was corrected to ref
         and lost refurbished (7-10). */
      if (!picked && !(i === parts.length - 1 && startsOf(word).length)) {
        const near = nearest(word);
        if (near && SYNONYMS[near]) {
          offer(SYNONYMS[near], 'typo');
          picked = primary(SYNONYMS[near]);
        } else if (near) {
          picked = near;
          kinds[near] = kinds[near] || 'typo';
        }
      }
      /* No word list loaded? Then a synonym is still safe — it was checked
         against the catalogue when it was written — but a stem is not. */
      if (!picked && !words() && SYNONYMS[word]) picked = SYNONYMS[word];
      if (picked && picked !== word) {
        changed.push([word, picked, kinds[picked] || 'synonym']);
        const grid = grids[picked] || [[picked]];
        for (let j = 0; j < grid.length; j += 1) {
          const ways = grid[j].filter(known);
          out.push(grid[j][0]);
          alternatives.push(ways.length ? ways : [grid[j][0]]);
        }
      } else {
        take(word);
      }
    }
    /* A word twice is one word (asked 23-9, after the same fix in the
       OpenCart search). The query is an AND of positions, so a repeated
       position adds nothing: it counts that word's score twice and it breaks
       the bonus for the typed words standing in a row in the title —
       "camera camera lens" no longer matches "… Camera Lens …", so the
       product that fits best drops. It happens for real: the OpenCart log
       holds "camera camera lens" 187 times and "a55 a55" 77 times, and a
       rule that puts a word in front of one already typed does the same.
       Compared on what a position may match, not on the word itself: two
       positions collapse only when they ask for exactly the same words. */
    const kept = [];
    const keptAlts = [];
    const seenKeys = Object.create(null);
    for (let i = 0; i < out.length; i += 1) {
      const key = (alternatives[i] && alternatives[i].length ? alternatives[i] : [out[i]]).join('|');
      /* "for" is no word to search on (6-10). Shopify's search leaves it out
         of its index, so a required title:"for" finds nothing: "for ipad pro
         13 (2024) display" gave 0 on 4Phones and the same without "for" 1.
         4Phones writes model names with it ("For iPad Air 11 (2025)"), and
         the titles' "For Apple …" invites typing it. Dropped unless it is
         all there is. */
      if (key === 'for' && out.length > 1) continue;
      if (seenKeys[key]) continue;
      seenKeys[key] = 1;
      kept.push(out[i]);
      keptAlts.push(alternatives[i]);
    }
    /* A model word and the number behind it may also stand glued (2-10).
       The catalogue is not of one mind: titles and tags say "Fold5" and never
       "Fold 5" (40 products against 0, measured on EMC), while the Flip has
       "Flip5" in its titles and "Flip 5" in its tags. The splitting above
       turns a typed fold5 into fold + 5, which then finds nothing, and
       somebody typing "fold 5" finds nothing either. So both positions also
       accept the glued form: a product that says Fold5 answers the fold and
       the 5 with that one word, and a product that says Fold 5 answers them
       as it always did. Nothing is replaced, so nothing that was found gets
       lost; the hand-written zflip7 rules stay for the z in front.
       After the collapsing above, on purpose: the glued form is no reason to
       call two positions the same. */
    for (let i = 0; i + 1 < kept.length; i += 1) {
      const head = kept[i];
      const tail = kept[i + 1];
      if (!MODEL_WORDS[head] || !/^[a-z]+$/.test(head) || !/^[0-9]+[a-z]{0,2}$/.test(tail)) continue;
      const glued = head + tail;
      if (keptAlts[i].indexOf(glued) < 0) keptAlts[i] = keptAlts[i].concat([glued]);
      if (keptAlts[i + 1].indexOf(glued) < 0) keptAlts[i + 1] = keptAlts[i + 1].concat([glued]);
    }
    /* The same the other way round: a number with a short model word behind
       it. splitModel turns 3ds into 3 + ds and 5se into 5 + se, while the
       shop writes "Nintendo 3DS XL" and "iPhone 5SE" glued, so every 3DS,
       2DS and 5SE search found nothing (21 of 22,344 terms in the model
       study, 2-10). Both positions accept the glued form again. Two letters
       at most: "17 pro" stays as it is, the catalogue never glues those.

       A single letter behind a number is glued as well, model word or not
       (6-10). The catalogue writes one phone both ways: ten titles say
       "Oppo Reno8 T 4G" and two say "Oppo Reno 8T 4G", so "reno8 t 4g" found
       ten and "reno8t 4g" twelve. The same goes for Reno4 Z against Reno 4Z,
       and the model tags glue it too. */
    for (let i = 0; i + 1 < kept.length; i += 1) {
      const head = kept[i];
      const tail = kept[i + 1];
      if (!/^[0-9]{1,3}$/.test(head)) continue;
      if (!/^[a-z]$/.test(tail) && !(/^[a-z]{2}$/.test(tail) && MODEL_WORDS[tail])) continue;
      const glued = head + tail;
      if (keptAlts[i].indexOf(glued) < 0) keptAlts[i] = keptAlts[i].concat([glued]);
      if (keptAlts[i + 1].indexOf(glued) < 0) keptAlts[i + 1] = keptAlts[i + 1].concat([glued]);
    }
    /* And the other way round again (6-10): a number and its letter typed
       glued, where the catalogue writes the letter apart. 4Phones' titles
       and model tags say "Reno8 T", so "reno8t 4g" (reno + 8t) found nothing
       while "reno8 t 4g" found twenty. The 8t also accepts the letter alone,
       and the word in front the number glued to it (reno8): "Reno8 T"
       answers both positions. Only right behind a model word, and never
       for 4G or 5G: "4g" asked as "g" as well would find every Moto G, and
       "kabel 2m" is no model. */
    for (let i = 1; i < kept.length; i += 1) {
      const m = /^([0-9]{1,3})([a-z])$/.exec(String(kept[i]));
      const head = String(kept[i - 1]);
      /* Nor an s: 6s, 14s, a50s are models of their own, written glued in
         the titles. Asked apart, "redmi note 14s" also asked note14 and a
         loose s, and found the Note 14 screens (7-10). */
      if (!m || m[2] === 'g' || m[2] === 's' || !MODEL_WORDS[head] || !/^[a-z]+$/.test(head)) continue;
      if (keptAlts[i].indexOf(m[2]) < 0) keptAlts[i] = keptAlts[i].concat([m[2]]);
      if (keptAlts[i - 1].indexOf(head + m[1]) < 0) keptAlts[i - 1] = keptAlts[i - 1].concat([head + m[1]]);
    }
    /* A half-typed word in the middle of a term is finished off as well
       (7-10): "iphone 11 disp pulled" asked for the word "disp", found
       nothing, and the 4Phones dropdown then fell back on Shopify's loose
       suggestions, refurbished displays and all. The words the catalogue has
       for it (startsOf: unknown word, three letters or more, ten words at
       most) ride along beside the typed one. The last word is left to the
       query builders, which already do this there and star it as well. */
    for (let i = 0; i + 1 < kept.length; i += 1) {
      if (keptAlts[i].length !== 1) continue;
      const starts = startsOf(String(kept[i]));
      if (starts.length) keptAlts[i] = keptAlts[i].concat(starts);
    }
    /* And a singular rides with its plural (7-10), the other way round from
       "chargers" above. Shopify's product types come from Akeneo in the
       plural (Back covers, Screens and Displays, Housings and frames) and a
       field match does not equate cover with covers: the iPhone back covers
       are titled "Back Glass", so "iphone 14 back cover" found none of them
       (0; with covers 49). Of 1,445 back covers, 292 carry "cover" only in
       their type. The own index already counts a plural type as singular. */
    for (let i = 0; i < keptAlts.length; i += 1) {
      const group = keptAlts[i];
      for (let j = 0, n = group.length; j < n; j += 1) {
        const forms = pluralsOf(String(group[j])).filter(known);
        for (let k = 0; k < forms.length; k += 1) {
          if (keptAlts[i].indexOf(forms[k]) < 0) keptAlts[i] = keptAlts[i].concat([forms[k]]);
        }
      }
    }
    /* The codes a typed code stands for ride beside it (codesFor). */
    for (let i = 0; i < kept.length; i += 1) {
      const hits = codeHits[String(kept[i])];
      /* an exact code (no other spelling) is kept whole, nothing to add */
      if (!hits || !hits.length) continue;
      for (let k = 0; k < hits.length; k += 1) if (keptAlts[i].indexOf(hits[k]) < 0) keptAlts[i] = keptAlts[i].concat([hits[k]]);
      changed.push([String(kept[i]), hits.join(' | '), 'code']);
    }
    return { words: kept, alternatives: keptAlts, changed: changed };
  }

  /* The shop's codes (7-10): model codes and manufacturer codes, from the
     tags (phones-search-codes.js, written by the app beside the word list). A
     repairer types the code on the part in his hand, often without its
     dashes and often only the start of it. Shopify and the own index both
     match a code whole, so "gh8239192cpa" and "gh82-3919" found nothing
     where GH82-39192C-PA was meant. Compared without dashes, slashes or
     dots: a typed code the shop has is asked in the shop's spelling; the
     last word, still being typed, is also asked as the codes it is the
     start of, from five characters and at most ten codes (more means it has
     barely begun). Only for a word with letters and digits: "a52" is a
     model, too short to be a code start anyone types on purpose. */
  let CODES = null;
  let CODE_KEYS = null;
  const codeKey = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
  function codeTable() {
    if (CODES) return CODES;
    const list = window.phonesSearchCodes;
    if (!Array.isArray(list)) return null;
    CODES = Object.create(null);
    for (let i = 0; i < list.length; i += 1) {
      const code = String(list[i]).toLowerCase();
      const key = codeKey(code);
      if (key.length < 4) continue;
      (CODES[key] = CODES[key] || []).push(code);
    }
    CODE_KEYS = Object.keys(CODES).sort();
    return CODES;
  }
  /* Is this word, as typed, one of the shop's codes? codesFor gives the
     other spellings of a code; this says the word is one itself. */
  function isShopCode(word) {
    const w = String(word);
    if (!/[0-9]/.test(w) || !/[a-z]/.test(w)) return false;
    const table = codeTable();
    const found = table && table[codeKey(w)];
    return !!found && found.indexOf(w) >= 0;
  }
  function codesFor(word, typing) {
    const w = String(word);
    const mixed = /[0-9]/.test(w) && /[a-z]/.test(w);
    /* A code being typed may not have reached its digits yet ("pot-lx" on
       the way to POT-LX1, seen 7-10); its dash says it is one all the same.
       Nor its letters: Xiaomi's part numbers start with digits ("5600010" on
       the way to 56000100K600 found nothing, 8-10). Seven digits or more,
       so a model number or a quantity is never taken for one; the SKUs are
       not in the list, so a half SKU finishes to nothing. */
    const digits = typing && /^[0-9]{7,}$/.test(w);
    if (!mixed && !digits && !(typing && /[a-z]/.test(w) && /[-/]/.test(w))) return [];
    const key = codeKey(w);
    if (key.length < 4) return [];
    const table = codeTable();
    if (!table) return [];
    if (mixed && table[key]) return table[key].filter(function (c) { return c !== w; });
    if (!typing || key.length < 5) return [];
    let lo = 0;
    let hi = CODE_KEYS.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (CODE_KEYS[mid] < key) lo = mid + 1;
      else hi = mid;
    }
    const out = [];
    for (let i = lo; i < CODE_KEYS.length && CODE_KEYS[i].indexOf(key) === 0; i += 1) {
      Array.prototype.push.apply(out, CODES[CODE_KEYS[i]]);
      if (out.length > 10) return [];
    }
    return out;
  }
  /* The list is some 50 kB, so it is fetched only once a search field is
     used (window.phonesSearchCodesUrl, set by the theme beside the scripts);
     a page nobody searches on never loads it. */
  function loadCodes() {
    if (window.phonesSearchCodes || loadCodes.started || !window.phonesSearchCodesUrl) return;
    loadCodes.started = true;
    const s = document.createElement('script');
    s.src = window.phonesSearchCodesUrl;
    s.async = true;
    document.head.appendChild(s);
  }
  if (document.addEventListener) {
    document.addEventListener('focusin', function (e) {
      const t = e.target;
      if (t && t.matches && t.matches('input[type="search"], input[name="q"]')) loadCodes();
    });
  }

  /* The catalogue words a half-typed last word may become, for Shopify's
     own search (6-10). Shopify does not widen a star inside a field:
     title:refu* finds nothing, and only from refurbish* on does it find the
     refurbished screens, by stemming. The app's index finishes a word
     itself, so EMC never noticed; 4Phones, and EMC whenever the index does
     not answer, found nothing for "iphone 11 refu". The query then asks
     these words whole beside the star.
     Only for a word the catalogue does not know (a known word is complete:
     display must not drag in displayport), three letters or more, letters
     only (a code is never half), and ten words at most: more means the word
     has barely begun, and the query would grow with every one of them. */
  const MAX_STARTS = 10;
  function startsOf(word) {
    const set = words();
    const w = String(word);
    if (!set || w.length < 3 || !/^[a-z]+$/.test(w) || set.has(w)) return [];
    const out = [];
    for (const c of set) {
      if (c.length <= w.length || c.indexOf(w) !== 0) continue;
      out.push(c);
      if (out.length > MAX_STARTS) return [];
    }
    return out.sort();
  }

  let VENDOR_WORDS = null;
  window.phonesSearchTerms = {
    rewrite: rewrite,
    startsOf: startsOf,
    codesFor: codesFor,
    loadCodes: loadCodes,
    known: known,
    synonyms: SYNONYMS,
    lang: lang,
    /* Two letters that mean a device: se, fe, xs, xr. The query drops a short
       trailing word as somebody still typing, and without this it dropped the
       model — "iphone se" went out as "iphone". */
    isModelWord: function (w) { return !!MODEL_WORDS[String(w).toLowerCase()]; },
    /* A word of one of the shop's brands: jk (JK Displays), jc, ny, 3m. The
       query drops a short trailing word as somebody still typing, and that
       sent "iphone 14 jk" out as "iphone 14", 982 products where the JK
       displays were meant (7-10). The brands come with the word list; a
       single letter out of one (the n and a of "N/A") is no brand. */
    isVendorWord: function (w) {
      if (!VENDOR_WORDS) {
        VENDOR_WORDS = Object.create(null);
        const list = window.phonesSearchVendors || [];
        for (let i = 0; i < list.length; i += 1)
          String(list[i]).toLowerCase().split(/[^a-z0-9]+/).forEach(function (part) { if (part.length > 1) VENDOR_WORDS[part] = true; });
      }
      return !!VENDOR_WORDS[String(w).toLowerCase()];
    },
  };
})();

/* When Shopify's search does not answer (20-10: its text search returned
   nothing in every shop for about an hour), the theme's search sections get
   no count and print the raw plural of their translation:
     {"one"=>"{{ count }} result found for “{{ terms }}”", "other"=>…}
   This puts a plain message in its place, in the language of the page, in
   the dropdown and on the search page alike. It lives here and not in the
   hook so it works with every version of the hook a theme runs. A theme can
   give its own wording in window.phonesSearchUnavailable. */
(function () {
  const RAW = /\{\s*"(?:zero|one|two|few|many|other)"\s*=>[\s\S]*?"\s*\}/;
  const TEXT = {
    en: 'Search is temporarily unavailable. Please try again in a few minutes.',
    nl: 'Zoeken is tijdelijk niet beschikbaar. Probeer het over een paar minuten opnieuw.',
    de: 'Die Suche ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in ein paar Minuten erneut.',
    fr: 'La recherche est temporairement indisponible. Veuillez réessayer dans quelques minutes.',
    es: 'La búsqueda no está disponible temporalmente. Inténtalo de nuevo en unos minutos.',
    it: 'La ricerca è temporaneamente non disponibile. Riprova tra qualche minuto.',
    pt: 'A pesquisa está temporariamente indisponível. Tente novamente dentro de alguns minutos.',
  };
  function message() {
    if (typeof window.phonesSearchUnavailable === 'string' && window.phonesSearchUnavailable) return window.phonesSearchUnavailable;
    const lang = String(document.documentElement.getAttribute('lang') || 'en').slice(0, 2).toLowerCase();
    return TEXT[lang] || TEXT.en;
  }
  function fix(root) {
    if (!root || !document.createTreeWalker || String(root.textContent || '').indexOf('=>') < 0) return;
    const walker = document.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
    const hits = [];
    while (walker.nextNode()) if (RAW.test(walker.currentNode.nodeValue)) hits.push(walker.currentNode);
    hits.forEach(function (node) {
      const el = node.parentNode;
      /* a hidden helper line (the count for screen readers) is emptied; the
         visible heading gets the message */
      const hidden = el && el.closest && el.closest('.hidden,[hidden],.visually-hidden');
      node.nodeValue = hidden ? '' : message();
      if (!hidden && el && el.setAttribute) el.setAttribute('data-phones-search-down', '');
    });
  }
  function start() {
    fix(document.body);
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function (list) {
      for (let i = 0; i < list.length; i += 1) {
        const added = list[i].addedNodes;
        for (let j = 0; j < added.length; j += 1) if (added[j].nodeType === 1) fix(added[j]);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  window.phonesSearchOutage = { fix: fix, message: message };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
